import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  from,
  HttpLink,
  InMemoryCache,
  split,
} from '@apollo/client';
import { loadErrorMessages, loadDevMessages } from '@apollo/client/dev';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';

import { setJWT } from '../redux/actions';
import configureStore from '../redux/store';
import { isJwtExpired } from '../utils/is-jwt-valid';

if (process.env.NODE_ENV !== 'production') {
  loadDevMessages();
  loadErrorMessages();
}

export default function GraphQLContainer(props) {
  const jwt = useSelector((state) => state.jwt);
  const dispatch = useDispatch();

  const client = useMemo(() => {
    const errorLink = onError(({ graphQLErrors, networkError }) => {
      if (process.env.NODE_ENV !== 'production') {
        if (graphQLErrors) console.log('[GraphQL Error]', graphQLErrors);
        if (networkError) console.log('[Network Error]', networkError);
      }

      // Abgelaufene Session -> JWT zurücksetzen -> Redirect zum Login (#13671).
      // Nur bei abgelaufenem Token ("Unauthorized" kommt auch bei falscher PIN);
      // verzögert, weil viele Queries gleichzeitig scheitern können.
      if (graphQLErrors?.some((error) => error.message === 'Unauthorized')) {
        setTimeout(() => {
          const currentJwt = configureStore().store.getState().jwt;
          if (currentJwt && isJwtExpired(currentJwt)) {
            dispatch(setJWT(null));
          }
        }, 0);
      }
    });

    const authMiddleware = new ApolloLink((operation, forward) => {
      operation.setContext(({ headers = {} }) => ({
        headers: {
          ...headers,
          authorization: jwt ? `Bearer ${jwt}` : null,
        },
      }));
      return forward(operation);
    });

    const wsLink = new GraphQLWsLink(
      createClient({
        url:
          window.location.protocol.replace('http', 'ws') +
          '//' +
          window.location.hostname +
          process.env.REACT_APP_GRAPHQL_WS,
        retryAttempts: Infinity, // Das beste Äquivalent zu "reconnect: true"
        keepAlive: 30_000,
        // Funktion statt Objekt: jeder (Re-)Connect nutzt das aktuelle Token (#13671)
        connectionParams: () => {
          const currentJwt = configureStore().store.getState().jwt;

          return {
            Authorization: currentJwt ? `Bearer ${currentJwt}` : null,
          };
        },
        lazy: true,
      })
    );

    const httpLink = new HttpLink({
      uri: process.env.REACT_APP_GRAPHQL_SERVER_URL,
    });

    const link = split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return (
          definition.kind === 'OperationDefinition' &&
          definition.operation === 'subscription'
        );
      },
      wsLink,
      httpLink
    );

    return new ApolloClient({
      link: from([errorLink, authMiddleware, link]),
      cache: new InMemoryCache({ resultCaching: false }),
      devtools: {
        enabled: process.env.REACT_APP_APOLLO_DEVTOOLS_ACTIVE,
      },
      defaultOptions: {
        watchQuery: {
          fetchPolicy: 'no-cache',
        },
        query: {
          fetchPolicy: 'no-cache',
        },
      },
    });
  }, [jwt, dispatch]);

  return <ApolloProvider client={client}>{props.children}</ApolloProvider>;
}
